import React, { useCallback, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { callAPI } from "../helper/apiUtils";
import LanguageContext from "../helper/LanguageContext";
import { translate } from "../Languages/index";
import { SUPPORTED_LANGUAGE_CODES, getBrowserLanguage, isGermanFamily } from "./auth/authLanguages";
import { AuthEye, AuthFieldMessage, AuthFrame, AuthHead, AuthInput, AuthNotice, AuthOr, AuthTitle } from "./auth/AuthPro";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// "Username*" → "Username", "*Password reset via …" → "Password reset via …" (the labels carry no asterisks).
const plain = (text) => String(text || "").replace(/^\*+\s*|\s*\*+$/g, "");

function ForgotPassword() {
  const history = useHistory();
  const { setLanguage: setContextLanguage } = React.useContext(LanguageContext);
  const [language, setLanguage] = useState(() => {
    const stored = localStorage.getItem("language");
    return stored && SUPPORTED_LANGUAGE_CODES.includes(stored) ? stored : getBrowserLanguage();
  });
  const t = useCallback((key) => translate(key, language), [language]);

  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [userPin, setUserPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingPlus, setLoadingPlus] = useState(false);
  // Inline errors per field ('email' | 'username' | 'userPin'), cleared by typing in that field.
  const [errors, setErrors] = useState({});
  // The e-mail request's answer: { ok, message } — ok shows the notice, otherwise the line under the field.
  const [emailResult, setEmailResult] = useState(null);
  const [plusError, setPlusError] = useState("");

  const clearError = (name) => setErrors((prev) => ({ ...prev, [name]: "" }));

  const handleChangeLanguage = (code) => {
    const langLower = String(code || "en").toLowerCase();
    const germanFamily = isGermanFamily(langLower);
    localStorage.setItem("switch", germanFamily ? "2" : "1");
    localStorage.setItem("language", langLower);
    localStorage.setItem("regiLang", germanFamily ? "2" : "1");
    setLanguage(langLower);
    if (setContextLanguage) setContextLanguage(langLower);
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setEmailResult(null);
    if (!email) {
      setErrors((prev) => ({ ...prev, email: t("invalid_fiels") }));
      return;
    }
    setLoading(true);
    try {
      const { data } = await callAPI("/auth/forgotPassword/", {}, "POST", { email });
      setEmailResult({ ok: !!data.status, message: data.message || data.messages });
    } catch (err) {
      console.log(err);
    }
    setLoading(false);
  };

  const submitPlus = async (e) => {
    e.preventDefault();
    setPlusError("");
    const next = {};
    if (!username) next.username = t("invalid_fiels");
    if (!userPin) next.userPin = t("invalid_fiels");
    if (next.username || next.userPin) {
      setErrors((prev) => ({ ...prev, ...next }));
      return;
    }
    setLoadingPlus(true);
    try {
      const { data } = await callAPI("/auth/forgotPassword/", null, "POST", { username, userPin });
      if (!data.status) {
        setPlusError(data.message);
      } else {
        window.open(data.message);
      }
    } catch (err) {
      setPlusError((err && err.message) || String(err));
    }
    setLoadingPlus(false);
  };

  const emailError = errors.email || (emailResult && !emailResult.ok ? emailResult.message : "");

  // Kjuup Pro · Forgot password (mobile ForgotPro, built on the Sign in pattern): back chevron and
  // language chip, "Forgot password?" over the subline, the e-mail field with "Send reset link"; then
  // the "or" divider and the Kjuup+ way (caption, Username + PIN, the secondary button).
  return (
    <AuthFrame testId="forgotPro">
      <AuthHead
        onBack={() => history.push("/")}
        backLabel={t("sign_in")}
        language={{ code: language, onChange: handleChangeLanguage, label: t("language") }}
      />

      <AuthTitle title={t("pro_auth_forgot_password")} sub={t("pro_auth_forgot_sub")} />

      {emailResult && emailResult.ok && emailResult.message ? (
        <AuthNotice tone="ok" icon="mark_email_read" text={emailResult.message} />
      ) : null}

      <form className="kj-auth__fields" onSubmit={submitEmail} noValidate>
        <AuthInput
          id="forgotEmail"
          aria-label={t("email")}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setEmailResult(null);
            clearError("email");
          }}
          placeholder={t("email")}
          error={!!emailError}
          message={emailError}
          type="email"
          autoComplete="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
        />
        <button
          type="submit"
          disabled={loading}
          aria-busy={loading}
          aria-label={loading ? t("submitting") : undefined}
          className="kj-btn kj-btn--primary kj-btn--lg kj-btn--block kj-auth__btn"
        >
          {loading ? <Spinner animation="border" size="sm" /> : t("pro_auth_send_link")}
        </button>
      </form>

      <AuthOr label={t("or")} />

      <form className="kj-auth__fields" onSubmit={submitPlus} noValidate>
        <div className="kj-auth__title-block">
          <div className="kj-auth__label">{t("v2_auth_plus_only")}</div>
          <p className="kj-auth__meta">{plain(t("only_for_kjuup"))}</p>
        </div>
        <AuthInput
          id="forgotUsername"
          aria-label={plain(t("username"))}
          placeholder={plain(t("username"))}
          value={username}
          onChange={(e) => {
            setPlusError("");
            // An e-mail address is no username: keep the field as it was and say so.
            if (EMAIL_PATTERN.test(e.target.value)) {
              setErrors((prev) => ({ ...prev, username: t("prevent_email") }));
              return;
            }
            setUsername(e.target.value);
            clearError("username");
          }}
          error={!!errors.username}
          message={errors.username}
          type="text"
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
        />
        <AuthInput
          id="forgotPin"
          aria-label="PIN"
          placeholder="PIN"
          value={userPin}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, "").slice(0, 6);
            setPlusError("");
            setUserPin(value);
            clearError("userPin");
          }}
          error={!!errors.userPin}
          message={errors.userPin}
          type={showPin ? "text" : "password"}
          inputMode="numeric"
          maxLength={6}
          autoComplete="off"
          trailing={
            <AuthEye
              shown={showPin}
              onToggle={() => setShowPin((v) => !v)}
              showLabel={t("pro_auth_show_pin")}
              hideLabel={t("pro_auth_hide_pin")}
            />
          }
        />
        {plusError ? <AuthFieldMessage text={plusError} /> : null}
        <button
          type="submit"
          disabled={loadingPlus}
          aria-busy={loadingPlus}
          aria-label={loadingPlus ? t("submitting") : undefined}
          className="kj-btn kj-btn--outline kj-btn--lg kj-btn--block kj-auth__btn"
        >
          {loadingPlus ? <Spinner animation="border" size="sm" /> : t("pro_auth_plus_reset")}
        </button>
      </form>
    </AuthFrame>
  );
}

export default ForgotPassword;
